import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  BellOff,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Download,
  Eraser,
  FileText,
  Filter,
  ExternalLink,
  Hourglass,
  Loader2,
  Mail,
  MailX,
  MailOpen,
  MousePointerClick,
  PauseCircle,
  PlayCircle,
  Receipt,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  ShieldCheck,
  StickyNote,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../lib/utils';
import PrivateLayout from '../../layouts/PrivateLayout';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import ConfirmModal from '../shared/ConfirmModal';
import BillingSchedulePanel from './BillingSchedulePanel';
import { useUser } from '../../../contexts/UserContext';
import { useUpdateClaim } from '../../../services/claims';
import {
  money,
  fmtDate,
  fmtDateTime,
  daysOverdue,
} from '../shared/billingFormat';
import {
  ClaimStatusText,
  DisputeMutedChip,
  EngagementIndicator,
  ExcludedChip,
  InvoiceStatusPill,
  PlanStateChip,
  RemindersPausedChip,
  Th,
} from '../shared/badges';
import {
  fetchAdminInvoicesExport,
  useFetchAdminInvoices,
  useFetchInvoiceEmails,
  useFetchInvoiceTiles,
  usePauseInvoiceReminders,
  useResumeInvoiceReminders,
  useSendInvoiceReminderNow,
  useUpdateInvoiceNotes,
} from '../../../services/billingAdmin';
import { buildCsv, downloadTextFile } from '../../../lib/csv';

// STATUS column filter options. Selections UNION (any checked option matches).
// Payment plans moved to the dedicated PLAN column (PLAN_OPTIONS below).
const STATUS_OPTIONS = [
  { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'paid', label: 'Paid' },
  { key: 'disputed', label: 'Disputed' },
  { key: 'paused', label: 'Reminders paused' },
  { key: 'excluded', label: 'Excluded from email' },
  { key: 'muted', label: 'Muted (any source)' },
  { key: 'draft', label: 'Draft' },
  { key: 'void', label: 'Void' },
  { key: 'uncollectible', label: 'Uncollectible' },
];

// PLAN column filter options (URL param pf; absent = unfiltered). Mirrors the
// server's PLAN_FILTERS vocabulary; buckets union like the other funnels.
const PLAN_OPTIONS = [
  { key: 'none', label: 'No plan' },
  { key: 'offered', label: 'Offered / unlocked' },
  { key: 'pending', label: 'Proposed / awaiting setup' },
  { key: 'active', label: 'Active' },
  { key: 'past_due', label: 'Past due' },
  { key: 'failed', label: 'Failed' },
];
// The quick tile's saved view: every invoice covered by a live plan.
const PLAN_TILE_FILTER = 'pending,active,past_due,failed';

// LAST REMINDER column filter options, evaluated against the last billing
// email or any billing email depending on the scope toggle.
const ENGAGEMENT_OPTIONS = [
  { key: 'nothing_sent', label: 'Nothing sent' },
  { key: 'never_opened', label: 'Never opened' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'bounced', label: 'Bounced' },
];

const AGING_BUCKETS = ['0-30', '31-60', '61-90', '90+'];

// CLAIM STATUS column filter options: the claim lifecycle, board order.
// Default view excludes Dead (collection abandoned; write-offs surface via
// the invoice-status Uncollectible option instead).
const CLAIM_STATUS_OPTIONS = [
  { key: 'intake', label: 'Intake' },
  { key: 'awaiting medical records', label: 'Awaiting Med. Recs.' },
  { key: 'in process', label: 'In Process' },
  { key: 'awaiting internal review', label: 'Awaiting Review' },
  { key: 'internal review approved', label: 'Review Approved' },
  { key: 'submitted to va', label: 'Submitted' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'payment plan', label: 'Payment Plan' },
  { key: 'paid', label: 'Paid' },
  { key: 'dispute hold', label: 'Dispute Hold' },
  { key: 'dead', label: 'Dead' },
];
const CLAIM_STATUS_KEYS = CLAIM_STATUS_OPTIONS.map((o) => o.key);
const CLAIM_STATUS_DEFAULT = CLAIM_STATUS_KEYS.filter((k) => k !== 'dead');

const CATEGORY_LABELS = {
  'billing.invoice.initial': 'Invoice email',
  'billing.invoice.reminder': 'Reminder',
};

// payment_plans.metadata->>'source' vocabulary (see paymentPlanSetup.js).
const PLAN_SOURCE_LABELS = {
  client_setup: 'Client self-setup',
  admin_setup: 'Set up by staff',
  client_request: 'Proposed by staff',
};

// Human labels for the CSV export (mirror the PlanStateChip vocabulary).
const PLAN_EXPORT_LABELS = {
  requested: 'Proposed',
  setup_pending: 'Awaiting setup',
  active: 'Active',
  past_due: 'Past due',
  failed: 'Failed',
  offered: 'Offered (auto)',
  unlocked: 'Unlocked by staff',
};

const engagementExportLabel = (inv) => {
  if (!inv.lastEmailAt) return 'Nothing sent';
  if (inv.lastBouncedAt) return 'Bounced';
  if (inv.lastClickedAt) return 'Clicked';
  if (inv.lastOpenedAt) return 'Opened';
  return 'Never opened';
};

// Quick-filter tiles: saved-filter shortcuts with live whole-book counts.
// Each tile's target params mirror the matching list filter exactly, so the
// tile's number always equals the rows a click lands on. "Open" is the
// default view, so it doubles as the reset tile.
const TILES = [
  {
    key: 'open',
    label: 'Open',
    icon: Receipt,
    tone: 'text-status-info-fg',
    // Active = solid tone with white text (the tile reads as "pressed").
    activeCls: 'border-blue-600 bg-blue-600',
    params: {
      st: null,
      cst: null,
      pf: null,
      eng: null,
      scope: null,
      aging: null,
      attn: null,
    },
  },
  {
    key: 'draft',
    label: 'Draft',
    icon: FileText,
    // Indigo: distinct from every other tone (slate is reserved for Muted).
    tone: 'text-indigo-500',
    activeCls: 'border-indigo-500 bg-indigo-500',
    params: {
      st: 'draft',
      cst: null,
      pf: null,
      eng: null,
      scope: null,
      aging: null,
      attn: null,
    },
  },
  {
    key: 'overdue',
    label: 'Overdue',
    icon: AlertTriangle,
    tone: 'text-status-warning-fg',
    // amber-600 (not 500): white text needs the darker step for contrast.
    activeCls: 'border-amber-600 bg-amber-600',
    params: {
      st: 'overdue',
      cst: null,
      pf: null,
      eng: null,
      scope: null,
      aging: null,
      attn: null,
    },
  },
  {
    key: 'paymentPlan',
    label: 'Payment plans',
    icon: CalendarClock,
    tone: 'text-violet-600',
    activeCls: 'border-violet-600 bg-violet-600',
    // st 'all' is required for tile-count parity: the tile counts covered
    // invoices in EVERY invoice status, but an absent st means the Open-only
    // default view which would hide non-open covered rows.
    params: {
      st: 'all',
      cst: null,
      pf: PLAN_TILE_FILTER,
      eng: null,
      scope: null,
      aging: null,
      attn: null,
    },
  },
  {
    key: 'stale75',
    label: 'Stale 75+',
    icon: Hourglass,
    // Cyan reads "gone cold": clients 75+ days overdue who have NEVER paid
    // anything and have no plan set up. Server-derived (attention=stale75).
    tone: 'text-cyan-700',
    activeCls: 'border-cyan-700 bg-cyan-700',
    params: {
      st: 'overdue',
      cst: null,
      pf: null,
      eng: null,
      scope: null,
      aging: null,
      attn: 'stale75',
    },
  },
  {
    key: 'disputed',
    label: 'Disputed',
    icon: ShieldAlert,
    tone: 'text-status-error-fg',
    activeCls: 'border-red-600 bg-red-600',
    params: {
      st: 'disputed',
      cst: null,
      pf: null,
      eng: null,
      scope: null,
      aging: null,
      attn: null,
    },
  },
  {
    key: 'muted',
    label: 'Muted',
    icon: BellOff,
    tone: 'text-slate-600',
    activeCls: 'border-slate-600 bg-slate-600',
    // Must open up claim statuses or dead-muted rows stay hidden.
    params: {
      st: 'muted',
      cst: 'all',
      pf: null,
      eng: null,
      scope: null,
      aging: null,
      attn: null,
    },
  },
];

// Column-header funnel filter (LeadHistory recipe): funnel icon with an
// active dot, checkbox dropdown with Clear, optional extra header content
// (used for the Last/Any scope toggle).
const HeaderFilter = ({
  label,
  options,
  selected,
  onToggle,
  onClear,
  headerExtra,
  showReset,
}) => {
  const [open, setOpen] = useState(false);
  const active = selected.length > 0;
  // "Default" resets the column to its default view; hidden while already there.
  const resetVisible = showReset === undefined ? active : showReset;

  return (
    <div className="relative inline-flex items-center gap-1">
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`Filter ${label}`}
        className={cn(
          'relative rounded p-0.5 transition-colors hover:text-foreground',
          active ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        <Filter className="h-3.5 w-3.5" />
        {active ? (
          <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
        ) : null}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-7 z-50 w-60 rounded-xl border border-border bg-card p-1 normal-case shadow-lg">
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-xs font-semibold text-foreground">
                Filter {label}
              </span>
              {resetVisible ? (
                <button
                  type="button"
                  onClick={onClear}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Default
                </button>
              ) : null}
            </div>
            {headerExtra}
            <div className="mx-3 my-1 h-px bg-border" />
            <div className="max-h-72 overflow-y-auto">
              {options.map((opt) => {
                const checked = selected.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => onToggle(opt.key)}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border',
                      )}
                    >
                      {checked ? <Check className="h-3 w-3" /> : null}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

// Sortable column header: chevrons show the active sort, muted double
// chevron marks the sortable-but-inactive columns.
const SortHeader = ({ label, sortKey, sortBy, sortDir, onSort }) => {
  const active = sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      title={`Sort by ${label.toLowerCase()}`}
      className={cn(
        'inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground',
        active ? 'text-foreground' : undefined,
      )}
    >
      {label}
      {active ? (
        sortDir === 'desc' ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronUp className="h-3.5 w-3.5" />
        )
      ) : (
        <ChevronsUpDown className="h-3 w-3 text-muted-foreground/60" />
      )}
    </button>
  );
};

const stepLabel = (email) => {
  if (email.category === 'billing.invoice.initial') return 'Invoice email';
  if (email.meta?.manual) return 'Manual reminder';
  if (email.reminderStep === 0) return 'Due date reminder';
  // The plan-available email logs with the -1 sentinel step (dedup index),
  // which used to render literally as "Day -1 reminder".
  if (email.reminderStep === -1) return 'Plan available email';
  if (email.reminderStep != null) return `Day ${email.reminderStep} reminder`;
  return CATEGORY_LABELS[email.category] || email.templateName || 'Email';
};

// One entry in the expanded reminder history.
const TimelineEntry = ({ email }) => {
  const eventChips = [];
  if (email.status === 'seeded') {
    eventChips.push(
      <span
        key="seed"
        className="inline-flex items-center gap-1 text-muted-foreground"
        title="Recorded at launch so pre-existing overdue invoices start the cadence from here instead of getting a catch-up email. Nothing was sent."
      >
        <ShieldCheck className="h-3.5 w-3.5" /> Launch baseline (no email sent)
      </span>,
    );
  } else if (email.status === 'suppressed') {
    eventChips.push(
      <span
        key="sup"
        className="inline-flex items-center gap-1 text-status-warning-fg"
      >
        <ShieldAlert className="h-3.5 w-3.5" />
        {email.meta?.reason === 'dispute_hold'
          ? 'Blocked: dispute hold'
          : email.meta?.reason === 'claim_dead'
            ? 'Blocked: claim dead'
            : 'Blocked: exclusion list'}
      </span>,
    );
  } else if (email.status === 'failed') {
    eventChips.push(
      <span
        key="fail"
        className="inline-flex items-center gap-1 text-status-error-fg"
      >
        <MailX className="h-3.5 w-3.5" /> Failed
      </span>,
    );
  } else {
    if (email.bouncedAt) {
      eventChips.push(
        <span
          key="bounce"
          className="inline-flex items-center gap-1 text-status-error-fg"
        >
          <MailX className="h-3.5 w-3.5" /> Bounced
        </span>,
      );
    }
    if (email.openedAt) {
      eventChips.push(
        <span
          key="open"
          className="inline-flex items-center gap-1 text-status-info-fg"
          title={fmtDateTime(email.openedAt)}
        >
          <MailOpen className="h-3.5 w-3.5" /> Opened
        </span>,
      );
    }
    if (email.clickedAt) {
      eventChips.push(
        <span
          key="click"
          className="inline-flex items-center gap-1 text-status-success-fg"
          title={fmtDateTime(email.clickedAt)}
        >
          <MousePointerClick className="h-3.5 w-3.5" /> Clicked
        </span>,
      );
    }
    if (!email.bouncedAt && !email.openedAt && !email.clickedAt) {
      eventChips.push(
        <span
          key="sent"
          className="inline-flex items-center gap-1 text-muted-foreground"
        >
          <Mail className="h-3.5 w-3.5" /> Sent, not opened yet
        </span>,
      );
    }
  }

  return (
    <div className="border-b border-border py-2 last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-foreground">
          {stepLabel(email)}
        </span>
        <span className="flex items-center gap-3 text-xs">{eventChips}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {fmtDateTime(email.createdAt)} · to {email.toEmail}
      </div>
    </div>
  );
};

// Claim statuses that may be placed on Dispute Hold (mirror of the state
// machine's entry rules in src/lib/claimHelpers.js).
const DISPUTE_HOLDABLE_STATUSES = ['invoiced', 'payment plan', 'paid'];

const DISPUTE_RELEASE_TARGETS = [
  {
    key: 'invoiced',
    label: 'Invoiced',
    desc: 'Resume collection. Reminders pick back up on the next sweep.',
  },
  {
    key: 'paid',
    label: 'Paid',
    desc: 'The dispute is resolved and the payment stands.',
  },
  {
    key: 'dead',
    label: 'Dead',
    desc: 'Give up on the claim. Also mark the invoice uncollectible in Stripe to write it off.',
  },
];

// Expanded panel: full email trail + manage actions for the invoice.
const InvoiceDetailPanel = ({ invoice, canManage }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useFetchInvoiceEmails(invoice.invoiceId);
  const pause = usePauseInvoiceReminders();
  const resume = useResumeInvoiceReminders();
  const sendNow = useSendInvoiceReminderNow();
  const updateClaim = useUpdateClaim();
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [disputeAction, setDisputeAction] = useState(null); // 'place' | 'release'
  const [disputeNote, setDisputeNote] = useState('');
  const [releaseTarget, setReleaseTarget] = useState('invoiced');

  const act = async (mutation, successMsg) => {
    try {
      const result = await mutation.mutateAsync(invoice.invoiceId);
      if (result?.sent === false && result?.duplicate) {
        toast.info(
          'A reminder for this invoice was sent moments ago; skipped the duplicate.',
        );
        return;
      }
      if (result?.sent === false && result?.disputeDivergence) {
        toast.info(
          "No reminder sent: this invoice's charge was disputed. Stripe shows it paid, but the balance here is still owed, so nothing was changed. Resolve the dispute outcome first.",
        );
        return;
      }
      if (result?.sent === false && result?.stripeStatus === 'missing') {
        toast.info(
          result?.reviewFlagged
            ? 'This invoice no longer exists in Stripe. Our copy was removed and its billing event is flagged Needs Review on the VA Calls page.'
            : 'This invoice no longer exists in Stripe. Our copy was removed to match.',
        );
        return;
      }
      if (result?.sent === false && result?.stripeStatus) {
        // Benign outcome: Stripe already settled this invoice; our row just
        // got synced to match, so the list refresh reflects it.
        toast.info(
          `No reminder needed: Stripe reports this invoice is already ${result.stripeStatus}. Our records have been updated to match.`,
        );
        return;
      }
      toast.success(successMsg);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Action failed');
    }
  };

  // Dispute Hold place/release rides the normal claim-status endpoint: the
  // route accepts claims:view_all and the state machine enforces
  // billing:manage + the required note server-side.
  const runClaimUpdate = async (payload, successMsg) => {
    try {
      await updateClaim.mutateAsync({ claimId: invoice.claimId, payload });
      toast.success(successMsg);
      setDisputeAction(null);
      setDisputeNote('');
      queryClient.invalidateQueries(['billing-admin-invoices']);
      // Dispute Hold place/release changes the Disputed + Muted tile counts.
      queryClient.invalidateQueries(['billing-invoice-tiles']);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not update the claim');
    }
  };

  const emails = data?.emails || [];
  const busy =
    pause.isLoading ||
    resume.isLoading ||
    sendNow.isLoading ||
    updateClaim.isLoading;

  // Written by the Stripe dispute webhook; manual holds have no stripeDispute.
  const stripeDispute = invoice.invoiceMeta?.stripeDispute || null;
  const disputeWon =
    stripeDispute?.status === 'won' ||
    stripeDispute?.status === 'warning_closed';

  return (
    // Same tint as the expanded parent row: the two read as one block under
    // the blue focus rail.
    <div className="bg-blue-50/60 px-6 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <span>
          Claims manager:{' '}
          <span className="font-medium text-foreground">
            {invoice.claimAssignedTo || 'Unassigned'}
          </span>
        </span>
        {invoice.previousRating != null && invoice.newRating != null ? (
          <span className="inline-flex items-center gap-1">
            Rating increase:{' '}
            <span className="inline-flex items-center gap-0.5 font-medium text-foreground">
              {invoice.previousRating}%
              <ArrowRight className="h-3 w-3" />
              {invoice.newRating}%
            </span>
          </span>
        ) : null}
      </div>
      {canManage ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {invoice.remindersPaused ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => act(resume, 'Reminders resumed for this invoice')}
            >
              <PlayCircle className="mr-1.5 h-4 w-4" /> Resume reminders
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => act(pause, 'Reminders paused for this invoice')}
            >
              <PauseCircle className="mr-1.5 h-4 w-4" /> Pause reminders
            </Button>
          )}
          {invoice.invoiceStatus === 'open' ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => setConfirmingSend(true)}
            >
              {sendNow.isLoading ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-1.5 h-4 w-4" />
              )}
              Send reminder now
            </Button>
          ) : null}
          {!invoice.disputeHold &&
          DISPUTE_HOLDABLE_STATUSES.includes(invoice.claimStatus) ? (
            <Button
              size="sm"
              variant="outline"
              className="border-status-error text-status-error-fg hover:bg-status-error-bg"
              disabled={busy}
              onClick={() => setDisputeAction('place')}
            >
              <ShieldAlert className="mr-1.5 h-4 w-4" /> Dispute Hold
            </Button>
          ) : null}
          {invoice.disputeHold ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                // A WON dispute means the payment stands: suggest Paid.
                setReleaseTarget(disputeWon ? 'paid' : 'invoiced');
                setDisputeAction('release');
              }}
            >
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Release hold
            </Button>
          ) : null}
          {invoice.invoiceStripeId ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                // Stripe DASHBOARD view (staff), not the hosted pay page the
                // client emails link to.
                window.open(
                  `https://dashboard.stripe.com/invoices/${invoice.invoiceStripeId}`,
                  '_blank',
                  'noopener',
                )
              }
            >
              <ExternalLink className="mr-1.5 h-4 w-4" /> Open in Stripe
            </Button>
          ) : null}
        </div>
      ) : null}

      {disputeAction === 'place' ? (
        <ConfirmModal
          title="Place on Dispute Hold"
          body={
            <div className="space-y-4">
              <div>
                Freezes ALL billing for{' '}
                <strong>
                  {invoice.userFirstName} {invoice.userLastName}
                </strong>
                : no invoice emails, no reminders, no payment plan charges, no
                automated billing. Release is manual and stays with Finance.
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Dispute note (required)
                </label>
                <textarea
                  value={disputeNote}
                  onChange={(e) => setDisputeNote(e.target.value)}
                  rows={3}
                  placeholder="What is the client disputing?"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>
          }
          confirmLabel="Place hold"
          destructive
          confirmDisabled={!disputeNote.trim()}
          isSaving={updateClaim.isLoading}
          onClose={() => {
            setDisputeAction(null);
            setDisputeNote('');
          }}
          onConfirm={() =>
            runClaimUpdate(
              {
                claimStatus: 'dispute hold',
                disputeHoldNote: disputeNote.trim(),
              },
              'Claim placed on Dispute Hold',
            )
          }
        />
      ) : null}

      {disputeAction === 'release' ? (
        <ConfirmModal
          title="Release Dispute Hold"
          body={
            <div className="space-y-4">
              <div>
                Where should{' '}
                <strong>
                  {invoice.userFirstName} {invoice.userLastName}
                </strong>
                &apos;s claim land? Billing email and payment plan charges
                resume based on the new status.
              </div>
              <div className="space-y-1.5">
                {DISPUTE_RELEASE_TARGETS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setReleaseTarget(opt.key)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                      releaseTarget === opt.key
                        ? 'border-primary bg-accent'
                        : 'border-border hover:bg-accent/50',
                    )}
                  >
                    <div className="text-sm font-medium text-foreground">
                      {opt.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {opt.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          }
          confirmLabel={`Release to ${releaseTarget}`}
          isSaving={updateClaim.isLoading}
          onClose={() => setDisputeAction(null)}
          onConfirm={() =>
            runClaimUpdate(
              { claimStatus: releaseTarget },
              'Dispute Hold released',
            )
          }
        />
      ) : null}

      {confirmingSend ? (
        <ConfirmModal
          title="Send reminder now"
          body={
            <div className="space-y-4">
              {invoice.remindersPaused ? (
                <div className="flex items-start gap-2 rounded-lg bg-status-warning-bg px-3 py-2 text-status-warning-fg">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Reminders are paused for this invoice.</span>
                </div>
              ) : null}
              <div>
                Send a payment reminder to <strong>{invoice.userEmail}</strong>{' '}
                right now? It includes a fresh payment link and does not change
                the automatic reminder schedule.
              </div>
            </div>
          }
          confirmLabel={
            invoice.remindersPaused ? 'Send anyway' : 'Send reminder'
          }
          confirmClassName={
            invoice.remindersPaused
              ? 'bg-status-warning-bg text-status-warning-fg hover:bg-status-warning-fg/70 hover:text-white'
              : undefined
          }
          isSaving={sendNow.isLoading}
          onClose={() => setConfirmingSend(false)}
          onConfirm={async () => {
            await act(sendNow, 'Reminder sent');
            setConfirmingSend(false);
          }}
        />
      ) : null}

      {invoice.disputeHold || stripeDispute ? (
        <div className="mb-4 rounded-lg border border-status-error-fg/20 bg-status-error-bg/40 px-4 py-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-status-error-fg">
            <ShieldAlert className="h-3.5 w-3.5" /> Dispute context
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">
                Disputed amount
              </div>
              <div className="font-medium text-foreground">
                {stripeDispute?.amount != null
                  ? money(stripeDispute.amount)
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Reason</div>
              <div className="text-foreground">
                {stripeDispute?.reason || '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">
                Dispute status
              </div>
              <div className="text-foreground">
                {stripeDispute?.status
                  ? disputeWon
                    ? `${stripeDispute.status} (resolved in our favor)`
                    : stripeDispute.status
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Hold placed</div>
              <div className="text-foreground">
                {invoice.disputeHoldAt
                  ? `${fmtDate(invoice.disputeHoldAt)} (${
                      invoice.disputeHoldSource === 'stripe_dispute'
                        ? 'formal Stripe dispute'
                        : 'manual hold'
                    })`
                  : '—'}
              </div>
            </div>
          </div>
          {invoice.disputeHoldNote ? (
            <div className="mt-2 text-xs text-muted-foreground">
              Note: {invoice.disputeHoldNote}
            </div>
          ) : null}
        </div>
      ) : null}

      {invoice.planState ? (
        <div className="mb-4 rounded-lg border border-violet-300/40 bg-violet-50/60 px-4 py-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-violet-700">
            <CalendarClock className="h-3.5 w-3.5" /> Payment plan
          </div>
          {invoice.paymentPlanStatus ? (
            <>
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Source</div>
                  <div className="text-foreground">
                    {PLAN_SOURCE_LABELS[invoice.planSource] || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Created</div>
                  <div className="text-foreground">
                    {invoice.planCreatedAt
                      ? fmtDate(invoice.planCreatedAt)
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Progress</div>
                  <div className="font-medium text-foreground">
                    {invoice.planPaidCount != null &&
                    invoice.planTotalCount != null
                      ? `${Number(invoice.planPaidCount)} of ${Number(invoice.planTotalCount)} paid`
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">
                    Next installment
                  </div>
                  <div className="text-foreground">
                    {invoice.planNextDueDate
                      ? `${fmtDate(invoice.planNextDueDate)}${
                          invoice.planNextAmount != null
                            ? ` for ${money(invoice.planNextAmount)}`
                            : ''
                        }`
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Card</div>
                  <div className="text-foreground">
                    {invoice.planCardLast4
                      ? `${invoice.planCardBrand || 'card'} ****${invoice.planCardLast4}`
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">
                    Plan remaining
                  </div>
                  <div className="font-medium text-foreground">
                    {invoice.planRemainingAmount != null
                      ? money(invoice.planRemainingAmount)
                      : '—'}
                  </div>
                </div>
              </div>
              {invoice.overrideEnabledAt ? (
                <div className="mt-2 text-xs text-muted-foreground">
                  Plan option was unlocked early by{' '}
                  {[invoice.overrideByFirstName, invoice.overrideByLastName]
                    .filter(Boolean)
                    .join(' ') || 'staff'}{' '}
                  on {fmtDate(invoice.overrideEnabledAt)}
                  {invoice.overrideNote ? ` (${invoice.overrideNote})` : ''}
                </div>
              ) : null}
            </>
          ) : invoice.planState === 'unlocked' ? (
            <div className="text-sm text-foreground">
              Unlocked early by{' '}
              {[invoice.overrideByFirstName, invoice.overrideByLastName]
                .filter(Boolean)
                .join(' ') || 'staff'}{' '}
              on{' '}
              {invoice.overrideEnabledAt
                ? fmtDate(invoice.overrideEnabledAt)
                : '—'}
              . The client can start a payment plan now; no plan has been set up
              yet.
              {invoice.overrideNote ? (
                <div className="mt-1 text-xs text-muted-foreground">
                  Note: {invoice.overrideNote}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-foreground">
              Plan available to the client since{' '}
              {invoice.planOfferedSince
                ? fmtDate(invoice.planOfferedSince)
                : '—'}{' '}
              (invoice age). No plan has been set up yet.
            </div>
          )}
        </div>
      ) : null}

      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Email history
      </div>
      {isLoading ? (
        <div className="py-4 text-center text-muted-foreground">
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        </div>
      ) : emails.length === 0 ? (
        <div className="py-3 text-sm text-muted-foreground">
          No billing emails have been sent for this invoice yet.
        </div>
      ) : (
        <div>
          {emails.map((email) => (
            <TimelineEntry key={email.outboundEmailId} email={email} />
          ))}
        </div>
      )}
    </div>
  );
};

// Notes column cell: the table only signals that a note exists; clicking
// opens a small anchored popover to read/edit it (Madison's billing notes,
// one editable field per invoice — invoiceMeta.billingNote; NOT invoiceNotes,
// which is the client-facing invoice description). Saving model: no
// per-keystroke autosave, but CLOSING the popover (outside click / X / Esc)
// saves a changed draft — click-away must never silently discard. An
// EMPTIED textarea is not saved on close (that would make closing
// destructive); the eraser + confirm is the only way to clear a note.
const NOTE_MAX_LENGTH = 500; // mirrors the server cap

const InvoiceNoteCell = ({ invoice, canManage }) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [draft, setDraft] = useState('');
  // Post-save feedback on the CELL icon (a green check flash): with
  // save-on-close there is no Save button, so the confirmation has to live
  // where the eye lands after the popover closes.
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const wrapRef = useRef(null);
  const textareaRef = useRef(null);
  const updateNotes = useUpdateInvoiceNotes();

  // The textarea grows with its content so the whole note is visible. A
  // typical max-length note fits under the cap; past it (e.g. lots of
  // newlines) the box switches to internal scrolling — the browser keeps the
  // caret in view while typing, so text is never hidden with no way back.
  const NOTE_BOX_MAX_HEIGHT = 320;
  useEffect(() => {
    if (!open || !textareaRef.current) return;
    const el = textareaRef.current;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, NOTE_BOX_MAX_HEIGHT)}px`;
    el.style.overflowY =
      el.scrollHeight > NOTE_BOX_MAX_HEIGHT ? 'auto' : 'hidden';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft]);

  const noteText = invoice.billingNote?.text || '';
  const hasNote = Boolean(noteText);

  // Event listeners fire with a render-frozen closure; the ref always holds
  // the latest draft/note so close-time saving never sees stale text.
  const latestRef = useRef({ draft: '', noteText: '', canManage: false });
  latestRef.current = { draft, noteText, canManage };

  const openPopover = () => {
    setDraft(noteText);
    setSavedFlash(false);
    setConfirmingClear(false);
    // Bottom rows: not enough viewport below → the popover opens upward
    // instead of running off-screen.
    const rect = wrapRef.current?.getBoundingClientRect();
    setOpenUp(Boolean(rect && window.innerHeight - rect.bottom < 460));
    setOpen(true);
  };

  const save = async (value) => {
    try {
      await updateNotes.mutateAsync({
        invoiceId: invoice.invoiceId,
        notes: value,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
      if (!value.trim()) setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not save the note');
    }
  };

  // Any close path saves a changed, non-empty draft (click-away must never
  // silently discard). An emptied textarea is NOT saved: closing must never
  // destroy a note — the eraser + confirm is the only clear path.
  const closeAndMaybeSave = () => {
    const latest = latestRef.current;
    const trimmed = latest.draft.trim();
    if (latest.canManage && trimmed && trimmed !== latest.noteText) {
      save(trimmed);
    }
    setOpen(false);
  };

  // Close on outside click / Escape while open.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        closeAndMaybeSave();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeAndMaybeSave();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="relative flex justify-center"
      // The cell lives inside a clickable row: nothing in here may toggle
      // the row expand.
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => (open ? closeAndMaybeSave() : openPopover())}
        aria-label={hasNote ? 'View billing note' : 'Add billing note'}
        title={
          hasNote
            ? `${noteText.slice(0, 80)}${noteText.length > 80 ? '...' : ''}`
            : canManage
              ? 'Add a billing note'
              : 'No billing note'
        }
        className="rounded-md p-1 transition-colors hover:bg-accent"
      >
        {savedFlash && !open ? (
          <Check className="h-4 w-4 text-emerald-600" />
        ) : (
          <StickyNote
            className={cn(
              'h-4 w-4',
              hasNote
                ? 'text-amber-600'
                : 'text-muted-foreground/25 hover:text-muted-foreground/60',
            )}
          />
        )}
      </button>
      {open ? (
        <div
          className={cn(
            'absolute right-0 z-30 w-80 rounded-lg border border-border bg-card p-3 text-left shadow-xl',
            openUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Billing note
            </span>
            <button
              type="button"
              onClick={closeAndMaybeSave}
              aria-label="Close"
              className="rounded-md p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {hasNote && invoice.billingNote?.updatedAt ? (
            <div className="mb-2 text-xs text-muted-foreground">
              Updated {fmtDateTime(invoice.billingNote.updatedAt)}
              {invoice.billingNote.updatedByName
                ? ` by ${invoice.billingNote.updatedByName}`
                : ''}
            </div>
          ) : null}
          {canManage ? (
            <>
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                autoFocus
                maxLength={NOTE_MAX_LENGTH}
                placeholder="Add a note about client contact..."
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:border-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
              />
              {confirmingClear ? (
                // Inline two-step confirm: stays anchored in the popover
                // instead of throwing a modal over the whole page.
                <div className="mt-1 flex items-center justify-between gap-2 rounded-md bg-red-50 px-2.5 py-1.5">
                  <span className="text-xs font-medium text-red-700">
                    Clear this note?
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      disabled={updateNotes.isLoading}
                      onClick={() => setConfirmingClear(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 bg-red-600 px-2 text-xs text-white hover:bg-red-700"
                      disabled={updateNotes.isLoading}
                      onClick={async () => {
                        await save('');
                        setConfirmingClear(false);
                      }}
                    >
                      {updateNotes.isLoading ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      ) : null}
                      Clear
                    </Button>
                  </span>
                </div>
              ) : (
                <div className="mt-0.5 flex min-h-[24px] items-center justify-between gap-2">
                  <span>
                    {hasNote ? (
                      <button
                        type="button"
                        disabled={updateNotes.isLoading}
                        onClick={() => setConfirmingClear(true)}
                        aria-label="Clear this note"
                        title="Clear this note"
                        className="rounded-md p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                      >
                        <Eraser className="h-4 w-4" />
                      </button>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      'text-[11px] tabular-nums',
                      draft.length >= NOTE_MAX_LENGTH
                        ? 'text-red-600'
                        : draft.length > NOTE_MAX_LENGTH - 50
                          ? 'text-amber-600'
                          : 'text-muted-foreground/60',
                    )}
                  >
                    {draft.length}/{NOTE_MAX_LENGTH}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="max-h-80 overflow-y-auto whitespace-pre-wrap text-sm text-foreground">
              {noteText || (
                <span className="text-muted-foreground">No note yet.</span>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const BillingInvoicesPage = () => {
  const { hasPermission } = useUser();
  const canManage = hasPermission('billing:manage');
  const [searchParams, setSearchParams] = useSearchParams();

  // st: comma list of STATUS_OPTIONS keys. Absent = the default working view
  // (open); the literal 'all' = no status filter.
  const stParam = searchParams.get('st');
  const statusFilter =
    stParam === null
      ? ['open']
      : stParam === 'all'
        ? []
        : stParam
            .split(',')
            .filter((s) => STATUS_OPTIONS.some((o) => o.key === s));
  // cst: comma list of claim statuses. Absent = the default view (everything
  // except dead); 'all' = no claim-status filter.
  const cstParam = searchParams.get('cst');
  const claimStatusFilter =
    cstParam === null
      ? CLAIM_STATUS_DEFAULT
      : cstParam === 'all'
        ? CLAIM_STATUS_KEYS
        : cstParam.split(',').filter((s) => CLAIM_STATUS_KEYS.includes(s));
  // pf: comma list of PLAN_OPTIONS keys. Absent = no plan filter (unlike st
  // there is no default selection, so no 'all' sentinel is needed).
  const planFilter = (searchParams.get('pf') || '')
    .split(',')
    .filter((p) => PLAN_OPTIONS.some((o) => o.key === p));
  const engagementFilter = (searchParams.get('eng') || '')
    .split(',')
    .filter((e) => ENGAGEMENT_OPTIONS.some((o) => o.key === e));
  const engagementScope = searchParams.get('scope') === 'any' ? 'any' : 'last';
  const aging = searchParams.get('aging') || '';
  // attn: the attention quick filter (only value today: stale75).
  const attention = searchParams.get('attn') === 'stale75' ? 'stale75' : '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const urlSearch = searchParams.get('q') || '';
  // Column sorting (server-side). Absent params = the default dueDate asc.
  const sortBy = searchParams.get('sort') || 'dueDate';
  const sortDir = searchParams.get('dir') === 'desc' ? 'desc' : 'asc';

  const [searchInput, setSearchInput] = useState(urlSearch);
  const [expanded, setExpanded] = useState(
    searchParams.get('invoiceId') || null,
  );
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const setParam = (patch) => {
    // Functional updater: the 350ms search debounce can fire from a render
    // whose searchParams are already stale (user clicked a tile/funnel during
    // the window) - patching prev instead of a closure keeps the click.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([key, value]) => {
          if (
            value === null ||
            value === undefined ||
            value === '' ||
            value === false
          ) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        });
        // Any filter change resets pagination.
        if (!('page' in patch)) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  // Debounced search -> URL (350ms, LeadsPage recipe).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== urlSearch) setParam({ q: searchInput || null });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Legacy URLs: 'payment_plan' used to live in the Status funnel. Map old
  // bookmarks (?st=payment_plan) onto the Plan filter once on mount; the
  // status parser above already drops the unknown key so nothing crashes
  // even without this.
  useEffect(() => {
    const raw = searchParams.get('st');
    if (!raw || raw === 'all') return;
    const parts = raw.split(',');
    if (!parts.includes('payment_plan')) return;
    const rest = parts.filter((s) => s !== 'payment_plan');
    setParam({
      st: rest.length ? rest.join(',') : 'all',
      pf: PLAN_TILE_FILTER,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusKey = statusFilter.join(',');
  const planKey = planFilter.join(',');
  const planIsDefault = planFilter.length === 0;
  const engagementKey = engagementFilter.join(',');
  // Default detection per column: hides the "Default" reset link when the
  // column is already showing its default view.
  const statusIsDefault =
    statusFilter.length === 1 && statusFilter[0] === 'open';
  const claimStatusIsDefault =
    claimStatusFilter.length === CLAIM_STATUS_DEFAULT.length &&
    CLAIM_STATUS_DEFAULT.every((s) => claimStatusFilter.includes(s));
  const engagementIsDefault =
    engagementFilter.length === 0 && engagementScope === 'last';
  // Absent param → let the server apply its own exclude-dead default; 'all'
  // and explicit lists pass through.
  const claimStatusesParam =
    cstParam === null
      ? undefined
      : cstParam === 'all'
        ? 'all'
        : claimStatusFilter.join(',');

  const params = useMemo(
    () => ({
      statuses: statusKey || undefined,
      claimStatuses: claimStatusesParam,
      planStates: planKey || undefined,
      engagement: engagementKey || undefined,
      engagementScope: engagementKey ? engagementScope : undefined,
      aging: aging || undefined,
      attention: attention || undefined,
      q: urlSearch || undefined,
      sortBy,
      sortDir,
      page,
      pageSize: 25,
    }),
    [
      statusKey,
      claimStatusesParam,
      planKey,
      engagementKey,
      engagementScope,
      aging,
      attention,
      urlSearch,
      sortBy,
      sortDir,
      page,
    ],
  );

  // Same column click flips direction; a new column starts ascending. The
  // default combo (dueDate asc) keeps the URL clean.
  const onSort = (key) => {
    const nextDir = sortBy === key && sortDir === 'asc' ? 'desc' : 'asc';
    const isDefault = key === 'dueDate' && nextDir === 'asc';
    setParam({
      sort: isDefault ? null : key,
      dir: nextDir === 'desc' ? 'desc' : null,
    });
  };

  // Toggle helpers: absent st means the 'open' default, so toggling must
  // materialize the current selection into the URL first.
  const toggleStatus = (key) => {
    const adding = !statusFilter.includes(key);
    const next = adding
      ? [...statusFilter, key]
      : statusFilter.filter((s) => s !== key);
    const patch = { st: next.length ? next.join(',') : 'all' };
    // Aging pins the server to open invoices; keeping it while the selection
    // has no open/overdue produces a contradictory (usually empty) list.
    const agingApplicable =
      next.length === 0 || next.includes('open') || next.includes('overdue');
    if (!agingApplicable) patch.aging = null;
    // Manually editing the status funnel means leaving the Stale saved view.
    patch.attn = null;
    // Muted includes dead claims by definition; open up the claim-status
    // filter exactly like the Muted tile does, or dead-muted rows stay
    // hidden and the count will not match the tile.
    if (adding && key === 'muted') patch.cst = 'all';
    setParam(patch);
  };
  const togglePlan = (key) => {
    const next = planFilter.includes(key)
      ? planFilter.filter((p) => p !== key)
      : [...planFilter, key];
    setParam({ pf: next.length ? next.join(',') : null });
  };
  const toggleEngagement = (key) => {
    const next = engagementFilter.includes(key)
      ? engagementFilter.filter((e) => e !== key)
      : [...engagementFilter, key];
    setParam({ eng: next.length ? next.join(',') : null });
  };
  const toggleClaimStatus = (key) => {
    const next = claimStatusFilter.includes(key)
      ? claimStatusFilter.filter((s) => s !== key)
      : [...claimStatusFilter, key];
    if (next.length === 0 || next.length === CLAIM_STATUS_KEYS.length) {
      setParam({ cst: 'all' });
    } else if (
      next.length === CLAIM_STATUS_DEFAULT.length &&
      CLAIM_STATUS_DEFAULT.every((s) => next.includes(s))
    ) {
      // Back to the default (everything but dead) → drop the param.
      setParam({ cst: null });
    } else {
      setParam({ cst: next.join(',') });
    }
  };

  const { data, isLoading, isError, refetch, isFetching } =
    useFetchAdminInvoices(params);

  const { data: tiles } = useFetchInvoiceTiles();

  const queryClient = useQueryClient();
  const refreshAll = async () => {
    // invalidateQueries resolves once the active queries finish refetching,
    // so the toast lands when the data on screen is actually fresh.
    await Promise.all([
      queryClient.invalidateQueries(['billing-admin-invoices']),
      queryClient.invalidateQueries(['billing-invoice-emails']),
      queryClient.invalidateQueries(['billing-invoice-tiles']),
    ]);
    toast.success('Invoices refreshed');
  };

  // Export: every row matching the current filters (server caps at 5000),
  // built into a CSV client-side. Columns = the visible table plus the
  // claims manager, the rating increase, and the billing note.
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetchAdminInvoicesExport(params);
      const rows = res?.data || [];
      if (!rows.length) {
        toast.info('Nothing to export for the current filters');
        return;
      }
      if ((res?.total || 0) > rows.length) {
        toast.info(
          `Export capped at ${rows.length} rows (${res.total} match the filters)`,
        );
      }
      const header = [
        'Client',
        'Email',
        'Claims manager',
        'Billed',
        'Remaining',
        'Previous rating',
        'New rating',
        'Status',
        'Plan',
        'Claim status',
        'Invoiced',
        'Due',
        'Days overdue',
        'Last reminder',
        'Engagement',
        'Notes',
      ];
      const csvRows = rows.map((inv) => {
        const overdue =
          inv.invoiceStatus === 'open' ? daysOverdue(inv.invoiceDueDate) : 0;
        return [
          `${inv.userFirstName || ''} ${inv.userLastName || ''}`.trim(),
          inv.userEmail || '',
          inv.claimAssignedTo || '',
          inv.invoiceBilledAmount != null ? inv.invoiceBilledAmount : '',
          inv.invoiceAmount != null ? inv.invoiceAmount : '',
          inv.previousRating != null ? `${inv.previousRating}%` : '',
          inv.newRating != null ? `${inv.newRating}%` : '',
          inv.invoiceStatus || '',
          inv.planDepositProcessing
            ? 'Deposit clearing'
            : PLAN_EXPORT_LABELS[inv.planState] || '',
          inv.claimStatus || '',
          inv.invoiceDate ? fmtDate(inv.invoiceDate) : '',
          inv.invoiceDueDate ? fmtDate(inv.invoiceDueDate) : '',
          overdue > 0 ? overdue : '',
          inv.lastReminderAt ? fmtDate(inv.lastReminderAt) : '',
          engagementExportLabel(inv),
          inv.billingNote?.text || '',
        ];
      });
      const stamp = new Date().toISOString().slice(0, 10);
      downloadTextFile({
        content: buildCsv(header, csvRows),
        filename: `invoices-export-${stamp}.csv`,
        type: 'text/csv;charset=utf-8',
      });
      toast.success(
        `Exported ${rows.length} invoice${rows.length === 1 ? '' : 's'}`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Tile activation: current URL params exactly match the tile's target.
  // An active search means the view is NOT the tile's saved view (its count
  // would not match the rows), so no tile reads as active while searching.
  const tileIsActive = (tile) => {
    if (urlSearch) return false;
    const st = searchParams.get('st');
    const cst = searchParams.get('cst');
    const pf = searchParams.get('pf');
    const attn = searchParams.get('attn');
    if (tile.key === 'open') {
      return (
        statusIsDefault &&
        claimStatusIsDefault &&
        planIsDefault &&
        engagementIsDefault &&
        !aging &&
        !attn
      );
    }
    return (
      st === tile.params.st &&
      (tile.params.cst || null) === cst &&
      (tile.params.pf || null) === pf &&
      (tile.params.attn || null) === attn
    );
  };

  const onTileClick = (tile) => {
    // A tile is a whole-book saved view: clear any active search so the
    // landing rows always equal the tile's count.
    setSearchInput('');
    if (tileIsActive(tile)) {
      // Clicking the active tile resets to the default view.
      setParam({
        st: null,
        cst: null,
        pf: null,
        eng: null,
        scope: null,
        aging: null,
        attn: null,
        q: null,
      });
    } else {
      setParam({ ...tile.params, q: null });
    }
  };

  const tileStats = (tile) => {
    const stat = tiles?.[tile.key];
    if (!stat) return null;
    return {
      count: stat.count,
      total: typeof stat.totalAmount === 'number' ? stat.totalAmount : null,
    };
  };

  const invoices = data?.data || [];
  const total = data?.total || 0;
  const pageSize = data?.pageSize || 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstShown = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastShown = Math.min(page * pageSize, total);
  // Whole-view money summary (server-computed across ALL pages of the
  // current filters) — the one line that answers "what am I looking at".
  const summary = data?.summary || null;

  // Aging only makes sense while looking at open money (the server pins the
  // bucket filter to open invoices anyway).
  const showAging =
    statusFilter.length === 0 ||
    statusFilter.includes('open') ||
    statusFilter.includes('overdue');

  return (
    <PrivateLayout>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">Invoices</h1>
            {tiles?.remindersEnabled === false ? (
              <Link
                to="/billing/settings"
                className="inline-flex items-center gap-1.5 rounded-full bg-status-info-bg px-3 py-1 text-xs font-medium text-status-info-fg no-underline transition-shadow hover:no-underline hover:ring-2 hover:ring-ring"
                title="ALL reminder emails are paused globally in Billing Settings. Click to manage."
              >
                <BellOff className="h-3.5 w-3.5" /> All reminders paused
              </Link>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            Every client invoice across the firm, with reminder delivery and
            engagement. The default view hides dead claims&apos; invoices; open
            the Claim Status filter to include them.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {TILES.map((tile) => {
            const active = tileIsActive(tile);
            const stats = tileStats(tile);
            const Icon = tile.icon;
            return (
              <button
                key={tile.key}
                type="button"
                onClick={() => onTileClick(tile)}
                title={
                  active
                    ? 'Showing this view. Click to reset to the default view.'
                    : `Filter to ${tile.label.toLowerCase()}`
                }
                className={cn(
                  'rounded-xl border px-4 py-3 text-left transition-all',
                  active
                    ? cn('shadow-md', tile.activeCls)
                    : 'border-border bg-card hover:-translate-y-0.5 hover:shadow-md',
                )}
              >
                <div
                  className={cn(
                    'flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide',
                    active ? 'text-white/85' : 'text-muted-foreground',
                  )}
                >
                  <Icon
                    className={cn(
                      'h-3.5 w-3.5',
                      active ? 'text-white' : tile.tone,
                    )}
                  />
                  {tile.label}
                </div>
                {stats ? (
                  <div className="mt-1 flex items-baseline gap-2">
                    <span
                      className={cn(
                        'text-xl font-bold',
                        active ? 'text-white' : 'text-foreground',
                      )}
                    >
                      {stats.count}
                    </span>
                    {stats.total != null ? (
                      <span
                        className={cn(
                          'text-xs',
                          active ? 'text-white/85' : 'text-muted-foreground',
                        )}
                      >
                        {money(stats.total)}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div
                    className={cn(
                      'mt-2 h-6 w-16 animate-pulse rounded',
                      active ? 'bg-white/25' : 'bg-muted',
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search client name or email..."
              className="px-9"
            />
            {searchInput ? (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                aria-label="Clear search"
                title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          {/* Whole-view money summary: server-computed across ALL pages of
              the current filters/search — the one line that answers "what am
              I looking at". The remaining figure is traffic-lit by how much
              of the billed total is still outstanding: under a third green,
              under two-thirds amber, above that rose. Full class strings
              (no template construction) so Tailwind keeps them at build. */}
          {summary ? (
            <div
              className="flex items-baseline rounded-lg border border-border bg-card px-3.5 py-1.5"
              title="Totals for every row matching the current filters, across all pages"
            >
              <span className="text-sm font-semibold text-foreground">
                {total} invoice{total === 1 ? '' : 's'}
              </span>
              <span className="mx-2.5 self-stretch border-l border-border" />
              <span
                className={cn(
                  'text-sm font-semibold',
                  (() => {
                    const pct =
                      summary.billedTotal > 0
                        ? summary.remainingTotal / summary.billedTotal
                        : 0;
                    // Tiny tolerance so an EXACT third (e.g. 2093.30 of
                    // 6279.90) lands in the better band instead of tipping
                    // over on float noise.
                    const EPS = 1e-6;
                    if (pct <= 1 / 3 + EPS)
                      return 'text-emerald-600/90 dark:text-emerald-400/90';
                    if (pct <= 2 / 3 + EPS)
                      return 'text-amber-600/90 dark:text-amber-400/90';
                    return 'text-rose-600/90 dark:text-rose-400/90';
                  })(),
                )}
              >
                {money(summary.remainingTotal)}
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  remaining
                </span>
              </span>
              <span className="mx-2.5 self-stretch border-l border-border" />
              <span className="text-xs font-medium text-blue-600/90 dark:text-blue-400/90">
                {money(summary.billedTotal)}
                <span className="ml-1 font-normal text-muted-foreground">
                  billed
                </span>
              </span>
            </div>
          ) : null}
          <div className="flex items-center gap-3">
            {/* The attn param survives funnel edits that un-light the tile;
                this pill keeps that state visible and dismissible. */}
            {attention === 'stale75' ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-700/10 px-2.5 py-1 text-xs font-medium text-cyan-800">
                <Hourglass className="h-3 w-3" />
                Stale 75+
                <button
                  type="button"
                  onClick={() => setParam({ attn: null })}
                  aria-label="Clear the Stale 75+ filter"
                  title="Clear the Stale 75+ filter"
                  className="rounded-full p-0.5 transition-colors hover:bg-cyan-700/15"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : null}
            {showAging ? (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Age
                </span>
                <div className="inline-flex overflow-hidden rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() => setParam({ aging: null })}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium transition-colors',
                      !aging
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    All
                  </button>
                  {AGING_BUCKETS.map((bucket) => (
                    <button
                      key={bucket}
                      type="button"
                      onClick={() =>
                        setParam({ aging: aging === bucket ? null : bucket })
                      }
                      className={cn(
                        'border-l border-border px-3 py-1.5 text-xs font-medium transition-colors',
                        aging === bucket
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                      )}
                    >
                      {bucket}d
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={exporting || total === 0}
              title="Download every row matching the current filters as CSV"
            >
              {exporting ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1.5 h-4 w-4" />
              )}
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScheduleOpen(true)}
              title="Upcoming reminders, plan offers, and installment charges"
            >
              <CalendarClock className="mr-1.5 h-4 w-4" />
              Schedule
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAll}
              disabled={isFetching}
              title="Refresh invoices and email activity"
            >
              <RefreshCw
                className={cn('mr-1.5 h-4 w-4', isFetching && 'animate-spin')}
              />
              Refresh
            </Button>
          </div>
        </div>

        <div className="overflow-visible rounded-xl border border-border bg-card">
          {/* Column dividers in the HEADER only (Nic's call): they anchor the
              columns without gridding up the data rows. The expander column
              (chevrons) reads as part of Last reminder, so no divider before
              it either. */}
          <table className="w-full text-sm [&_th]:border-r [&_th]:border-border/50 [&_th:last-child]:border-r-0 [&_th:nth-last-child(2)]:border-r-0">
            <thead className="bg-card">
              <tr className="border-b border-border">
                <Th>Client</Th>
                <Th className="text-right">
                  <SortHeader
                    label="Billed"
                    sortKey="billed"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={onSort}
                  />
                </Th>
                <Th className="text-right">
                  <SortHeader
                    label="Remaining"
                    sortKey="remaining"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={onSort}
                  />
                </Th>
                <Th className="text-center">
                  <HeaderFilter
                    label="Status"
                    options={STATUS_OPTIONS}
                    selected={statusFilter}
                    onToggle={toggleStatus}
                    onClear={() => setParam({ st: null })}
                    showReset={!statusIsDefault}
                  />
                </Th>
                <Th className="text-center">
                  <HeaderFilter
                    label="Plan"
                    options={PLAN_OPTIONS}
                    selected={planFilter}
                    onToggle={togglePlan}
                    onClear={() => setParam({ pf: null })}
                    showReset={!planIsDefault}
                  />
                </Th>
                <Th className="text-center">
                  <HeaderFilter
                    label="Claim Status"
                    options={CLAIM_STATUS_OPTIONS}
                    selected={claimStatusFilter}
                    onToggle={toggleClaimStatus}
                    onClear={() => setParam({ cst: null })}
                    showReset={!claimStatusIsDefault}
                  />
                </Th>
                <Th>
                  <SortHeader
                    label="Invoiced"
                    sortKey="invoiced"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={onSort}
                  />
                </Th>
                <Th>
                  <SortHeader
                    label="Due"
                    sortKey="dueDate"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={onSort}
                  />
                </Th>
                <Th className="w-14 text-center">Notes</Th>
                <Th className="text-center">
                  <HeaderFilter
                    label="Last reminder"
                    options={ENGAGEMENT_OPTIONS}
                    selected={engagementFilter}
                    onToggle={toggleEngagement}
                    onClear={() => setParam({ eng: null, scope: null })}
                    showReset={!engagementIsDefault}
                    headerExtra={
                      <div className="px-3 pb-1.5 pt-0.5">
                        <div className="inline-flex w-full overflow-hidden rounded-md border border-border text-xs">
                          <button
                            type="button"
                            onClick={() => setParam({ scope: null })}
                            className={cn(
                              'flex-1 px-2 py-1 font-medium transition-colors',
                              engagementScope === 'last'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                            )}
                          >
                            Last email
                          </button>
                          <button
                            type="button"
                            onClick={() => setParam({ scope: 'any' })}
                            className={cn(
                              'border-l border-border px-2 py-1 font-medium transition-colors',
                              engagementScope === 'any'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                            )}
                          >
                            Any email
                          </button>
                        </div>
                      </div>
                    }
                  />
                </Th>
                <Th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {isError ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <AlertTriangle className="h-6 w-6 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Could not load invoices.
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => refetch()}
                      >
                        <RefreshCw className="mr-2 h-4 w-4" /> Retry
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : isLoading && !data ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-10 text-center text-muted-foreground"
                  >
                    <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-10 text-center text-muted-foreground"
                  >
                    {statusFilter.length ||
                    planFilter.length ||
                    engagementFilter.length ||
                    aging ||
                    urlSearch
                      ? 'No invoices match the current filters.'
                      : 'No invoices yet.'}
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => {
                  const isOpenRow = expanded === inv.invoiceId;
                  const overdueDays =
                    inv.invoiceStatus === 'open'
                      ? daysOverdue(inv.invoiceDueDate)
                      : 0;
                  // Same helper, different anchor: days since INVOICING (the
                  // payment-plan eligibility clock).
                  const invoiceAgeDays = inv.invoiceDate
                    ? daysOverdue(inv.invoiceDate)
                    : -1;
                  return (
                    <React.Fragment key={inv.invoiceId}>
                      <tr
                        className={cn(
                          'cursor-pointer border-b border-border last:border-0',
                          // Expanded: merge with the detail panel below (no
                          // divider) under a shared blue focus tint; hover
                          // stays on collapsed rows only.
                          isOpenRow
                            ? 'border-b-0 bg-blue-50/60'
                            : 'hover:bg-accent/40',
                        )}
                        onClick={() =>
                          setExpanded(isOpenRow ? null : inv.invoiceId)
                        }
                      >
                        <td
                          className={cn(
                            'px-4 py-3',
                            // Inset shadow (not a border) so the rail takes no
                            // layout space and columns never shift on expand.
                            isOpenRow &&
                              'shadow-[inset_3px_0_0_0_theme(colors.blue.600)]',
                          )}
                        >
                          <div className="flex items-center gap-1.5 font-medium text-foreground">
                            {inv.userFirstName} {inv.userLastName}
                            {inv.claimStatus === 'dead' ? (
                              <DisputeMutedChip reason="dead" />
                            ) : inv.disputeHold ? (
                              <DisputeMutedChip />
                            ) : inv.remindersPaused ? (
                              <RemindersPausedChip />
                            ) : null}
                            {inv.emailExcluded ? <ExcludedChip /> : null}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {inv.userEmail}
                          </div>
                          <Link
                            to={`/admin/profile/${inv.claimId}?tab=billing`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-primary hover:underline"
                          >
                            View claim billing
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-foreground">
                          {/* No fallback to invoiceAmount: that is the
                              REMAINING balance (possibly $0), which reads as
                              a wrong issued total. Unknown = show nothing. */}
                          {inv.invoiceBilledAmount != null
                            ? money(inv.invoiceBilledAmount)
                            : '—'}
                          {inv.previousRating != null &&
                          inv.newRating != null ? (
                            // The increase this invoice bills for (from the
                            // claim billing event) — the amount's context.
                            <div
                              className="flex items-center justify-end gap-0.5 text-xs font-normal text-muted-foreground"
                              title="Rating increase this invoice bills for"
                            >
                              {inv.previousRating}%
                              <ArrowRight className="h-3 w-3" />
                              {inv.newRating}%
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right text-foreground">
                          {money(inv.invoiceAmount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <InvoiceStatusPill status={inv.invoiceStatus} />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <PlanStateChip
                            planState={inv.planState}
                            paidCount={inv.planPaidCount}
                            totalCount={inv.planTotalCount}
                            depositProcessing={inv.planDepositProcessing}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <ClaimStatusText status={inv.claimStatus} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-foreground">
                            {fmtDate(inv.invoiceDate)}
                          </div>
                          {inv.invoiceStatus === 'open' &&
                          invoiceAgeDays >= 0 ? (
                            // Age since invoicing is the payment-plan clock
                            // (offer opens at 60 days), so it earns a sub-line.
                            <div className="text-xs text-muted-foreground">
                              {invoiceAgeDays === 0
                                ? 'today'
                                : `${invoiceAgeDays} day${invoiceAgeDays === 1 ? '' : 's'} old`}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-foreground">
                            {fmtDate(inv.invoiceDueDate)}
                          </div>
                          {overdueDays > 0 ? (
                            <div className="text-xs font-medium text-status-error-fg">
                              {overdueDays} days overdue
                            </div>
                          ) : null}
                        </td>
                        <td className="px-2 py-3 text-center">
                          <InvoiceNoteCell
                            invoice={inv}
                            canManage={canManage}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-xs text-muted-foreground">
                            {inv.lastReminderAt
                              ? fmtDate(inv.lastReminderAt)
                              : inv.initialSentAt
                                ? `Invoice email ${fmtDate(inv.initialSentAt)}`
                                : 'Nothing sent'}
                          </div>
                          <EngagementIndicator
                            sentAt={inv.lastEmailAt}
                            openedAt={inv.lastOpenedAt}
                            clickedAt={inv.lastClickedAt}
                            bounced={Boolean(inv.lastBouncedAt)}
                          />
                        </td>
                        <td className="px-4 py-3 text-center text-muted-foreground">
                          {isOpenRow ? (
                            <ChevronUp className="h-4 w-4 text-blue-600" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </td>
                      </tr>
                      {isOpenRow ? (
                        // Heavier bottom border closes the expanded block.
                        <tr className="border-b-2 border-border last:border-0">
                          <td
                            colSpan={11}
                            className="p-0 shadow-[inset_3px_0_0_0_theme(colors.blue.600)]"
                          >
                            <InvoiceDetailPanel
                              invoice={inv}
                              canManage={canManage}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {total === 0
              ? 'No invoices'
              : `Showing ${firstShown}–${lastShown} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setParam({ page: page - 1 })}
              className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setParam({ page: page + 1 })}
              className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {scheduleOpen ? (
          <BillingSchedulePanel onClose={() => setScheduleOpen(false)} />
        ) : null}
      </div>
    </PrivateLayout>
  );
};

export default BillingInvoicesPage;
